import { createClient } from '@supabase/supabase-js';
import { developmentFetch, developmentMode } from '@/lib/development-environment';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY must be set');
}

// Lock en memoria (cola de promesas) para serializar las secciones críticas de
// auth (refresh de token, getSession). Sustituye al `navigator.locks` por
// defecto —que podía quedar RETENIDO y deadlockear getSession() ("Cargando…"
// eterno)— SIN caer en un passthrough puro. El passthrough no serializaba nada:
// dos refrescos concurrentes (típico al montar varias queries) podían rotar el
// refresh token a la vez → uno falla → la sesión se invalida → las peticiones
// siguientes salían como ANÓNIMAS → RLS bloqueaba los writes y la edición
// "revertía" en silencio. Esta cola garantiza un solo refresh a la vez, en
// memoria (sin locks de navegador → sin deadlock, sin cruce entre pestañas).
let authLockChain: Promise<unknown> = Promise.resolve();
function inMemoryAuthLock<T>(_name: string, _acquireTimeout: number, fn: () => Promise<T>): Promise<T> {
  const run = authLockChain.then(fn, fn);
  // La cola avanza pase lo que pase (éxito o error) para no atascarse nunca.
  authLockChain = run.then(() => undefined, () => undefined);
  return run as Promise<T>;
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  global: { fetch: developmentFetch },
  auth: {
    ...(developmentMode ? { storageKey: `pelip-${developmentMode}-session` } : {}),
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    lock: inMemoryAuthLock,
  },
});
